import React, { useEffect, useState } from 'react';
import { Target, ChevronDown, Megaphone, Gift, MousePointerClick, Link2, BarChart3, Crosshair, Layers, HelpCircle } from 'lucide-react';
import { getGoalMap, getCampaignGoal, setCampaignGoal, coachWhy } from '../utils/api';

const Row = ({ icon: Icon, label, children, testId }) => (
  <div className="flex items-start gap-3" data-testid={testId}>
    <div className="p-1.5 bg-black rounded-lg text-lime mt-0.5 shrink-0"><Icon size={15} /></div>
    <div>
      <p className="text-xs text-muted uppercase tracking-wide mb-0.5">{label}</p>
      <div className="text-sm text-light-gray">{children}</div>
    </div>
  </div>
);

export const CampaignGoalPanel = ({ campaignId, onGoalChange, onAssetChipClick }) => {
  const [goalMap, setGoalMap] = useState(null);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);
  const [why, setWhy] = useState(null);

  const toggleWhy = () => {
    const next = !whyOpen;
    setWhyOpen(next);
    if (next && !why) {
      coachWhy(campaignId).then(({ data }) => setWhy(data)).catch(() => setWhyOpen(false));
    }
  };

  useEffect(() => {
    let alive = true;
    Promise.all([getGoalMap(), getCampaignGoal(campaignId)])
      .then(([mapRes, goalRes]) => {
        if (!alive) return;
        setGoalMap(mapRes.data);
        if (goalRes.data.selected_goal) {
          setSelected(goalRes.data.selected_goal);
          onGoalChange?.(goalRes.data.selected_goal, mapRes.data[goalRes.data.selected_goal]);
        }
      })
      .catch((e) => console.error('goal load failed', e));
    return () => { alive = false; };
  }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = async (goalId) => {
    if (!goalId) return;
    setSelected(goalId);
    onGoalChange?.(goalId, goalMap[goalId]);
    setWhy(null);
    setSaving(true);
    try {
      await setCampaignGoal(campaignId, goalId);
      if (whyOpen) {
        const { data } = await coachWhy(campaignId);
        setWhy(data);
      }
    } catch (e) {
      console.error('goal save failed', e);
    } finally {
      setSaving(false);
    }
  };

  if (!goalMap) return null;
  const plan = selected ? goalMap[selected] : null;

  return (
    <div id="recommended-plan" className="bg-card-bg border border-divider rounded-xl p-6 mb-8" data-testid="campaign-goal-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className="text-white font-bold text-xl flex items-center gap-2">
          <Crosshair size={18} className="text-orange" /> What do you want this campaign to do?
        </h3>
        {saving && <span className="text-xs text-gray">Saving…</span>}
      </div>

      <div className="relative max-w-md mb-1">
        <select
          data-testid="goal-selector"
          value={selected || ''}
          onChange={(e) => handleSelect(e.target.value)}
          className="w-full appearance-none bg-black border-2 border-lime/50 rounded-lg px-4 py-3 text-white font-semibold focus:border-lime focus:outline-none cursor-pointer"
        >
          <option value="" disabled>Choose your goal…</option>
          {Object.entries(goalMap).map(([id, g]) => (
            <option key={id} value={id}>{g.label}</option>
          ))}
        </select>
        <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-lime pointer-events-none" />
      </div>

      {!plan && (
        <p className="text-sm text-gray mt-3" data-testid="goal-empty-hint">
          Pick a goal and GritClicks will show you exactly which assets, channels, offer and tracking to use.
        </p>
      )}

      {plan && (
        <div className="mt-5 p-5 rounded-lg bg-black border border-lime/30" data-testid="recommended-plan-panel">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2">
              <Target size={16} className="text-lime" />
              <p className="text-white font-bold text-lg">Recommended Plan for This Goal</p>
            </div>
            <button
              type="button"
              onClick={toggleWhy}
              data-testid="why-recommended-btn"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-lime hover:text-white transition-colors shrink-0"
            >
              <HelpCircle size={14} />
              {whyOpen ? 'Hide explanation' : 'Why did you recommend this?'}
            </button>
          </div>
          <p className="text-sm text-gray mb-5">{plan.why}</p>

          {whyOpen && (
            <div className="mb-6 p-4 rounded-lg bg-lime/5 border border-lime/20" data-testid="why-recommended-panel">
              {!why ? (
                <p className="text-sm text-muted">Loading explanation…</p>
              ) : (
                <div className="space-y-3 text-sm text-light-gray leading-relaxed">
                  <p className="text-white font-semibold">{why.summary}</p>
                  <div>
                    <p className="text-xs text-muted uppercase tracking-wide mb-1">Why these assets</p>
                    <ul className="space-y-1">
                      {why.asset_stack.map((a) => (
                        <li key={a.asset} className="flex gap-2"><span className="text-lime shrink-0">•</span><span><b className="text-white">{a.asset}</b> — {a.reason}</span></li>
                      ))}
                    </ul>
                  </div>
                  <p><b className="text-white">Channels:</b> {why.channels_reason}</p>
                  <p><b className="text-white">Offer:</b> {why.offer_reason}</p>
                  <p><b className="text-white">CTA:</b> {why.cta_reason}</p>
                  <p><b className="text-white">KPIs:</b> {why.kpi_reason}</p>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            <Row icon={Layers} label="Recommended Asset Stack" testId="plan-asset-stack">
              <div className="flex flex-wrap gap-1.5 mt-1">
                {plan.recommended_asset_stack.map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => onAssetChipClick?.(a)}
                    title={`Jump to ${a} in the asset bank below`}
                    data-testid={`stack-chip-${a.toLowerCase().replace(/\s+/g, '-')}`}
                    className="px-2.5 py-1 rounded-full bg-lime/10 border border-lime/30 text-lime text-xs font-semibold hover:bg-lime hover:text-black transition-colors cursor-pointer"
                  >{a}</button>
                ))}
              </div>
            </Row>
            <Row icon={Megaphone} label="Recommended Channels" testId="plan-channels">
              <ul className="list-disc list-inside space-y-0.5 mt-1">
                {plan.recommended_channels.map((c) => <li key={c}>{c}</li>)}
              </ul>
            </Row>
            <Row icon={Gift} label="Recommended Offer" testId="plan-offer">{plan.recommended_offer}</Row>
            <Row icon={MousePointerClick} label="Recommended Call to Action" testId="plan-cta">{plan.recommended_cta}</Row>
            <Row icon={Link2} label="How to Track It" testId="plan-tracking">{plan.recommended_tracking_method}</Row>
            <Row icon={BarChart3} label="Numbers That Matter (KPIs)" testId="plan-kpi">
              <div className="flex flex-wrap gap-1.5 mt-1">
                {plan.recommended_kpi.map((k) => (
                  <span key={k} className="px-2.5 py-1 rounded-full bg-orange/10 border border-orange/30 text-orange text-xs font-semibold">{k}</span>
                ))}
              </div>
            </Row>
            <Row icon={Crosshair} label="Google / Meta Objective" testId="plan-objective">{plan.recommended_objective}</Row>
          </div>
        </div>
      )}
    </div>
  );
};
